import * as React from 'react';
import { useDispatch } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { StatusCode } from '../../../../utils/api';
import { PageNames } from '../../../../utils/app-constants';
import { CreateRunForDeliveryFlyout } from '../components/CreateRunForDeliveryFlyout';
import { MetricTable } from '../../../../production/components/MetricTable';
import { MetricTotals } from '../../../../production/components/MetricTotals';
import { MetricsDetailFlyout } from '../../../../production/components/MetricsDetailFlyout';
import { PlanningType } from '../../../../production/models/enums/planning-type';
import { MetricsDeliveryExport } from '../../../../production/models/metrics/metrics-delivery-export-document';
import { UnplannedDeliveryMetricsGroup } from '../../../../production/models/metrics/unplanned-delivery-metrics-group';
import { UnplannedDeliveryOrder } from '../../../../production/models/metrics/unplanned-delivery-order';
import { CellMetric } from '../../../../production/models/shared/metric-base';
import { MetricRunningTotal } from '../../../../production/models/shared/metric-running-total';
import { ProductionSvc } from '../../../../production/service';

interface IProps extends IBaseSceneProps {
    exportFlag: number;
    setButtonState: any;
}

export const ProductionPlanningShipmentsScene = (props : IProps) => {

    const dispatch = useDispatch();
    const [tasks, setTasks] = React.useState<UnplannedDeliveryMetricsGroup[]>([]);
    const [service] = React.useState(new ProductionSvc());
    const [isCreateOpen, setIsCreateOpen] = React.useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [selecteds, setSelecteds] = React.useState<UnplannedDeliveryMetricsGroup[]>([]);
    const [selectedTotal, setSelectedTotal] = React.useState<MetricRunningTotal>(new MetricRunningTotal());
    const [cellData, setCellData] = React.useState<any[]>([]);
    const [showRowTotal, setShowRowTotal] = React.useState<boolean>(false);

    const [orders, setOrders] = React.useState<UnplannedDeliveryOrder[]>([]);
    const [flyoutTitle, setFlyoutTitle] = React.useState<string>('');

    const parseCells = (data : any[]) => {
        const celltable : any[] = [];
        data.forEach((itm : UnplannedDeliveryMetricsGroup) => {
            celltable.push([...itm.METRIC_LIST]);
        });

        setCellData(celltable);
    };

    async function getItems() {
        setTasks([]);
        setCellData([]);
        setSelecteds([]);
        setSelectedTotal(new MetricRunningTotal());
        const tms = await service.getUnplannedDeliveryTasks();
        if (tms.status === StatusCode.Success) {
            setTasks(tms.data);
            parseCells(tms.data);
        }
    }

    React.useEffect(() => {
        getItems();
    }, []);

    React.useEffect(() => {
        if (props.exportFlag > 0) {
            const req = new MetricsDeliveryExport();
            dispatch(getDocumentHelper(req));
        }
    }, [props.exportFlag]);

    /**
     * Close the create flyout
     */
    const closeCreateFlyout = async (refresh : boolean) => {
        setIsCreateOpen(false);
        if (refresh) {
            await getItems();
        }
    };
        
    /**
     * Close the detail flyout
     */
     const closeDetailFlyout = async () => {
        setIsDetailsOpen(false);
    };

    const openDetailFlyout = async () => {

        if (!selecteds || selecteds.length > 1) {
            return;
        }

        const dId = (selecteds[0] as UnplannedDeliveryMetricsGroup).shipmentId;
        const dName = (selecteds[0] as UnplannedDeliveryMetricsGroup).shipmentName;

        const results = await service.getUnplannedDeliveryOrders(dId);
        if (results.status === StatusCode.Success) {
            setOrders(results.data);

            const title = `${ dName } [${ dId }]`;
            setFlyoutTitle(title);
            
            setIsDetailsOpen(true);
        }
    };

    const onCellChange = (cells : any[], fullRows: any[]) => {
        setCellData(cells);

        const totalV2 = new MetricRunningTotal();
        cells.forEach((itm : CellMetric[]) => {

            itm.forEach((citm : CellMetric, cellIndx : number) => {

                if (cellIndx < 1) {
                    return;
                }

                if (citm.isCurrentlySelected === true && citm.jobType !== undefined && citm.jobType >= 0) {
                    for (let i = 0; i < totalV2.METRIC_LIST.length; i++) {
                        if (totalV2.METRIC_LIST[i].jobType === citm.jobType) {
                            totalV2.METRIC_LIST[i].quantity += citm.quantity;
                            break;
                        }
                    }
                }
            });
        });

        if (fullRows.length > 0) {            
            setShowRowTotal(true);

            const dealers : any[] = [];
            fullRows.forEach((itm : any) => (
                dealers.push(tasks[itm.index])
            ));

            setSelecteds(dealers);
        }
        else {
            setShowRowTotal(false);
        }

        setSelectedTotal(totalV2);
    };

    return (<PageContent classes="production-module" pageName={PageNames.PRODUCTION_PLANNING_SHIPMENTS}>
            <PageBody>
                
                <MetricTable 
                    runs={ tasks } 
                    cells={ cellData }
                    onCellChange={ onCellChange }
                    onlyRowSelect={ true }
                    showLegend={ false }
                    onViewDetails={ openDetailFlyout }
                    onStartProduction={ () => setIsCreateOpen(true) }
                    headers={['Shipment ID', 'Name', 'Departure' ]}
                    nonMetricPropCount={3}
                />

                <MetricTotals 
                    totals={ selectedTotal} 
                    shouldShow={ selecteds.length > 0 }
                />

                <CreateRunForDeliveryFlyout 
                    taskGroups={ selecteds }
                    isOpen={ isCreateOpen }
                    onClose={ closeCreateFlyout }
                />

                <MetricsDetailFlyout
                    orders={ orders }
                    title={ flyoutTitle }
                    type={ PlanningType.Delivery }
                    isOpen={ isDetailsOpen }
                    onClose={ closeDetailFlyout }
                />
                
            </PageBody>
        </PageContent>);
    
};
